import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Security, Lock } from '@mui/icons-material';

import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import { LoggerService } from '../services/LoggerService';

const schema = yup.object({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
});

const twoFactorSchema = yup.object({
  code: yup.string().required('2FA code is required').length(6, 'Code must be 6 digits'),
});

type FormData = yup.InferType<typeof schema>;
type TwoFactorData = yup.InferType<typeof twoFactorSchema>;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { login, setLoading, setError } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [loginStep, setLoginStep] = useState<'credentials' | 'twofactor'>('credentials');
  const [tempToken, setTempToken] = useState<string>('');
  const [username, setUsername] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const {
    register: register2FA,
    handleSubmit: handleSubmit2FA,
    formState: { errors: errors2FA },
  } = useForm<TwoFactorData>({
    resolver: yupResolver(twoFactorSchema),
  });

  const onSubmitCredentials = async (data: FormData) => {
    try {
      LoggerService.debug('Login form submitted with data:', data);
      setIsLoading(true);
      setLoading(true);
      setUsername(data.username);

      LoggerService.debug('Calling authService.login...');
      const response = await authService.login(data);
      LoggerService.debug('Login response received:', response);

      if (response.requires_2fa) {
        // User has 2FA enabled, proceed to 2FA step
        setTempToken(response.temp_token);
        setLoginStep('twofactor');
        enqueueSnackbar('Please enter your 2FA code', { variant: 'info' });
      } else {
        // No 2FA required, proceed with normal login
        LoggerService.debug('Setting tokens first...');
        
        // Set tokens FIRST so they're available for the getCurrentUser call
        login(
          response.access_token, 
          response.refresh_token, 
          response.family_id, 
          response.expires_in
        );
        
        // Get token directly from response to avoid store timing issues
        const token = response.access_token;
        LoggerService.debug('Token from response (first 20 chars):', token ? token.substring(0, 20) + '...' : 'None');
        
        LoggerService.debug('Now calling authService.getCurrentUser with token...');
        const user = await authService.getCurrentUser(token);
        LoggerService.debug('Current user received:', user);

        // Update the user data in the store
        useAuthStore.getState().setUser(user);
        enqueueSnackbar('Login successful!', { variant: 'success' });
        navigate('/dashboard');
      }
    } catch (error: any) {
      LoggerService.error('Login error:', error);
      setError(error.message || 'Login failed');
      enqueueSnackbar(error.message || 'Login failed', { variant: 'error' });
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const onSubmitTwoFactor = async (data: TwoFactorData) => {
    try {
      setIsLoading(true);
      setLoading(true);

      LoggerService.debug('Submitting 2FA code:', data.code);
      const response = await authService.verifyTwoFactor(data.code, tempToken);
      LoggerService.debug('2FA verification response:', response);

      // Set tokens first, then get user data
      LoggerService.debug('Setting tokens first...');
      
      // Set tokens FIRST so they're available for the getCurrentUser call
      login(
        response.access_token, 
        response.refresh_token, 
        response.family_id, 
        response.expires_in
      );
      
      LoggerService.debug('Now calling authService.getCurrentUser...');
      const user = await authService.getCurrentUser();
      LoggerService.debug('Current user received:', user);

      // Update the user data in the store
      useAuthStore.getState().setUser(user);
      enqueueSnackbar('Login successful!', { variant: 'success' });
      navigate('/dashboard');
    } catch (error: any) {
      LoggerService.error('2FA verification error:', error);
      setError(error.message || '2FA verification failed');
      enqueueSnackbar(error.message || 'Invalid 2FA code', { variant: 'error' });
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setLoginStep('credentials');
    setTempToken('');
    setUsername('');
  };

  const steps = ['Enter Credentials', 'Two-Factor Authentication'];

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
            <Security sx={{ fontSize: 40, mr: 1, color: 'primary.main' }} />
            <Typography component="h1" variant="h4" align="center">
              AmudFin
            </Typography>
          </Box>

          <Typography component="h2" variant="h5" align="center" gutterBottom>
            Sign In
          </Typography>

          <Stepper activeStep={loginStep === 'credentials' ? 0 : 1} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {loginStep === 'credentials' ? (
            <Box component="form" onSubmit={handleSubmit(onSubmitCredentials)} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                autoComplete="username"
                autoFocus
                {...register('username')}
                error={!!errors.username}
                helperText={errors.username?.message}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                {...register('password')}
                error={!!errors.password}
                helperText={errors.password?.message}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
              <Box textAlign="center">
                <Link component={RouterLink} to="/register" variant="body2">
                  Don't have an account? Sign Up
                </Link>
              </Box>
            </Box>
          ) : (
            <Box>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Lock sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Two-Factor Authentication</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please enter the 6-digit code from your authenticator app for user: <strong>{username}</strong>
                  </Typography>
                </CardContent>
              </Card>

              <Box component="form" onSubmit={handleSubmit2FA(onSubmitTwoFactor)} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="code"
                  label="2FA Code"
                  placeholder="123456"
                  autoComplete="one-time-code"
                  autoFocus
                  {...register2FA('code')}
                  error={!!errors2FA.code}
                  helperText={errors2FA.code?.message}
                  inputProps={{ maxLength: 6 }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Verify & Sign In'}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleBackToCredentials}
                  disabled={isLoading}
                >
                  Back to Credentials
                </Button>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Need help?</strong> If you don't have access to your authenticator app, 
                  you can use one of your backup codes instead of the 6-digit code.
                </Typography>
              </Alert>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;